/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS Email Marketing — inline drill tables, ports of the reference
   InlineContactTable.tsx / InlineCompanyTable.tsx: a filterable, paginated list
   rendered directly beneath whatever was clicked (KPI card, funnel stage,
   segment row), with HubSpot contact/company deep-links.
   ──────────────────────────────────────────────────────────────────────────── */
import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, IconButton, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useGetEmailMarketingCompaniesQuery, useGetEmailMarketingContactsQuery } from '../../redux/growthosEmailMarketingApiSlice'
import { GX_BORDER, GX_COLORS, GX_INK_500, GX_INK_900 } from '../growthos-shared/GrowthOsUi'
import { GxFilterInput, gxBodyCellSx, gxHeadCellSx, gxInt } from '../growthos-shared/GxPagedTable'

const PAGE_SIZE = 50

const recordUrl = (hub, obj, id) => (hub && id ? `https://${hub.uiDomain}/contacts/${hub.portalId}/record/${obj}/${id}` : undefined)

function DrillShell({ title, subtitle, filter, setFilter, filterPlaceholder, onClose, children, footer }) {
  return (
    <Box sx={{ border: `1px solid ${GX_COLORS.indigo}2e`, borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden', boxShadow: '0 1px 2px rgba(15,23,42,0.05)' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 2, py: 1.5, borderBottom: `1px solid ${GX_BORDER}` }}>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_900 }}>{title}</Typography>
          <Typography sx={{ fontSize: 12, color: GX_INK_500, mt: 0.25 }}>{subtitle}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GxFilterInput value={filter} onChange={setFilter} placeholder={filterPlaceholder} width={224} />
          <IconButton size="small" onClick={onClose} aria-label={`Close ${title}`} title="Close table"><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </Box>
      <TableContainer sx={{ maxHeight: 512, overflow: 'auto' }}>{children}</TableContainer>
      {footer}
    </Box>
  )
}

function Pager({ page, setPage, total }) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const current = Math.min(page, totalPages)
  if (total <= PAGE_SIZE) return null
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 2, py: 1.25, borderTop: `1px solid ${GX_BORDER}` }}>
      <Typography sx={{ fontSize: 12, color: GX_INK_500 }}>
        Showing {gxInt((current - 1) * PAGE_SIZE + 1)}–{gxInt(Math.min(current * PAGE_SIZE, total))} of {gxInt(total)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" disabled={current === 1} onClick={() => setPage(Math.max(1, current - 1))} aria-label="Previous page"><ChevronLeftIcon fontSize="small" /></IconButton>
        <Typography sx={{ fontSize: 12, color: GX_INK_500 }}>Page {current} of {totalPages}</Typography>
        <IconButton size="small" disabled={current === totalPages} onClick={() => setPage(Math.min(totalPages, current + 1))} aria-label="Next page"><ChevronRightIcon fontSize="small" /></IconButton>
      </Box>
    </Box>
  )
}

const pageOf = (rows, page) => {
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const current = Math.min(page, totalPages)
  return rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)
}

/** request = { metric, label, period, from?, to?, category?, leader? } */
export function InlineContactTable({ request, onClose }) {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const requestKey = JSON.stringify(request)
  const { data, isFetching } = useGetEmailMarketingContactsQuery(request)
  useEffect(() => setPage(1), [requestKey, q])

  const all = useMemo(() => data?.contacts || [], [data])
  const t = q.trim().toLowerCase()
  const filtered = useMemo(() => (t
    ? all.filter((c) => `${c.name || ''} ${c.email || ''} ${c.company || ''} ${c.leader} ${c.category}`.toLowerCase().includes(t))
    : all), [all, t])
  const rows = pageOf(filtered, page)
  const hub = data?.hubspot

  return (
    <DrillShell
      title={request.label}
      subtitle={isFetching && !data ? 'Loading contacts…' : t ? `${gxInt(filtered.length)} of ${gxInt(data?.count ?? 0)} contacts` : `${gxInt(data?.count ?? 0)} contacts`}
      filter={q} setFilter={setQ} filterPlaceholder="Filter contacts…" onClose={onClose}
      footer={<Pager page={page} setPage={setPage} total={filtered.length} />}
    >
      <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={gxHeadCellSx} title="Contact name and email.">Contact</TableCell>
            <TableCell sx={gxHeadCellSx} title="Company name.">Company</TableCell>
            <TableCell sx={gxHeadCellSx} title="Sales POC parsed from the last campaign name.">Sales POC</TableCell>
            <TableCell sx={gxHeadCellSx} title="Target category.">Category</TableCell>
            <TableCell sx={gxHeadCellSx} title="Lifetime opens for this contact (HubSpot stores lifetime counters, not per-period).">Opens</TableCell>
            <TableCell sx={gxHeadCellSx} title="Latest of last reply / click / open / send.">Last Activity</TableCell>
            <TableCell sx={gxHeadCellSx} title="Opens the record in HubSpot.">HubSpot</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isFetching && !data && <TableRow><TableCell colSpan={7} align="center" sx={{ ...gxBodyCellSx, color: GX_INK_500 }}>Loading contacts…</TableCell></TableRow>}
          {data && rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ ...gxBodyCellSx, color: GX_INK_500 }}>No contacts match.</TableCell></TableRow>}
          {rows.map((c) => {
            const companyHref = c.hubspotCompanyId ? recordUrl(hub, '0-2', c.hubspotCompanyId) : undefined
            const contactHref = recordUrl(hub, '0-1', c.hubspotContactId)
            return (
              <TableRow key={c.hubspotContactId} hover>
                <TableCell sx={gxBodyCellSx}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: GX_INK_900 }}>{c.name || c.email || c.hubspotContactId}</Typography>
                  {c.email && <Typography sx={{ fontSize: 11, color: GX_INK_500 }}>{c.email}</Typography>}
                </TableCell>
                <TableCell sx={gxBodyCellSx}>
                  {c.company
                    ? (companyHref ? <Link href={companyHref} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 12 }}>{c.company}</Link> : c.company)
                    : '—'}
                </TableCell>
                <TableCell sx={gxBodyCellSx}>{c.leader}</TableCell>
                <TableCell sx={gxBodyCellSx}>{c.category}</TableCell>
                <TableCell sx={gxBodyCellSx}>{gxInt(c.opens)}</TableCell>
                <TableCell sx={gxBodyCellSx}>{c.lastActivity || '—'}</TableCell>
                <TableCell sx={gxBodyCellSx}>
                  {contactHref
                    ? <Link href={contactHref} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Contact <OpenInNewIcon sx={{ fontSize: 12 }} /></Link>
                    : '—'}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </DrillShell>
  )
}

export function InlineCompanyTable({ period, from, to, onClose }) {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const { data, isFetching } = useGetEmailMarketingCompaniesQuery({ period, from, to })
  useEffect(() => setPage(1), [period, from, to, q])

  const all = useMemo(() => data?.companies || [], [data])
  const t = q.trim().toLowerCase()
  const filtered = useMemo(() => (t ? all.filter((c) => (c.company || '').toLowerCase().includes(t)) : all), [all, t])
  const rows = pageOf(filtered, page)
  const hub = data?.hubspot

  return (
    <DrillShell
      title="Companies reached"
      subtitle={isFetching && !data ? 'Loading companies…' : t ? `${gxInt(filtered.length)} of ${gxInt(data?.count ?? 0)} companies` : `${gxInt(data?.count ?? 0)} companies`}
      filter={q} setFilter={setQ} filterPlaceholder="Filter companies…" onClose={onClose}
      footer={<Pager page={page} setPage={setPage} total={filtered.length} />}
    >
      <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={gxHeadCellSx} title="Company name.">Company</TableCell>
            <TableCell sx={gxHeadCellSx} title="Number of contacts.">Contacts</TableCell>
            <TableCell sx={gxHeadCellSx} title="Σ lifetime emails delivered (bounces excluded).">Delivered</TableCell>
            <TableCell sx={gxHeadCellSx} title="Contacts who opened at least one marketing email.">Opened</TableCell>
            <TableCell sx={gxHeadCellSx} title="Contacts who clicked at least one marketing email.">Clicked</TableCell>
            <TableCell sx={gxHeadCellSx} title="Contacts who replied to at least one marketing email.">Replied</TableCell>
            <TableCell sx={gxHeadCellSx} title="Opens the record in HubSpot.">HubSpot</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isFetching && !data && <TableRow><TableCell colSpan={7} align="center" sx={{ ...gxBodyCellSx, color: GX_INK_500 }}>Loading companies…</TableCell></TableRow>}
          {data && rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ ...gxBodyCellSx, color: GX_INK_500 }}>No companies match.</TableCell></TableRow>}
          {rows.map((c, i) => {
            const href = c.hubspotCompanyId ? recordUrl(hub, '0-2', c.hubspotCompanyId) : undefined
            return (
              <TableRow key={c.hubspotCompanyId || `${c.company}-${i}`} hover>
                <TableCell sx={{ ...gxBodyCellSx, fontWeight: 600, color: GX_INK_900 }}>{c.company}</TableCell>
                <TableCell sx={gxBodyCellSx}>{gxInt(c.contacts)}</TableCell>
                <TableCell sx={gxBodyCellSx}>{gxInt(c.delivered)}</TableCell>
                <TableCell sx={gxBodyCellSx}>{gxInt(c.opened)}</TableCell>
                <TableCell sx={gxBodyCellSx}>{gxInt(c.clicked)}</TableCell>
                <TableCell sx={gxBodyCellSx}>{gxInt(c.replied)}</TableCell>
                <TableCell sx={gxBodyCellSx}>
                  {href
                    ? <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Company <OpenInNewIcon sx={{ fontSize: 12 }} /></Link>
                    : '—'}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </DrillShell>
  )
}
